import { useState, useEffect } from "react";
import { Mic, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function MicPermissionModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const checkPermission = async () => {
      // Check if we've already asked for permission
      const hasRequestedPermission = localStorage.getItem("mic_permission_requested");
      
      // Se não tiver sido solicitado ainda e o navegador suportar mediaDevices
      if (!hasRequestedPermission && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        let shouldShowModal = true;
        
        try {
          if (navigator.permissions && navigator.permissions.query) {
            const result = await navigator.permissions.query({ name: 'microphone' } as unknown as PermissionDescriptor);
            if (result.state === 'granted' || result.state === 'denied') {
              shouldShowModal = false;
              localStorage.setItem("mic_permission_requested", "true");
            }
          }
        } catch (error) {
          console.log("Permission query not supported", error);
        }
        
        if (shouldShowModal) {
          // Dá um pequeno atraso para não abrir o modal assim que a tela carregar bruscamente
          timer = setTimeout(() => {
            setOpen(true);
          }, 1500);
        }
      }
    };

    checkPermission();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem("mic_permission_requested", "true");
  };

  const requestPermission = async () => {
    try {
      // Solicita o acesso ao microfone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Se tiver sucesso, já para as tracks para não ficar capturando o áudio atoa
      stream.getTracks().forEach(track => track.stop());
      
      toast.success("Microfone habilitado! 🎉", {
        description: "Agora você pode usar comandos de voz com a assistente."
      });
      handleClose();
    } catch (error) {
      console.error("Erro ao solicitar permissão de microfone:", error);
      
      // Se o usuário negou a permissão ou houve erro
      toast.info("Acesso ao microfone negado.", {
        description: "Você poderá habilitá-lo mais tarde no ícone de cadeado na barra de endereço."
      });
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleClose();
    }}>
      <DialogContent className="sm:max-w-md text-center">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Mic className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-xl sm:text-2xl font-bold text-center">
            Assistente Financeira por Voz 🎙️
          </DialogTitle>
          <DialogDescription className="text-center pt-2 text-base">
            O aplicativo agora conta com uma assistente inteligente! Para usar os comandos de voz e registrar suas despesas falando, precisamos de acesso ao seu microfone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col gap-3 mt-4">
          <Button onClick={requestPermission} size="lg" className="w-full">
            Habilitar Microfone
          </Button>
          <Button onClick={handleClose} variant="ghost" size="lg" className="w-full">
            Agora não
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
